// =====================================================================
// src/core/store.ts
//
// Estado global del app con patrón observable mínimo (sin librerías).
//
// Stores disponibles:
//   - sesionStore:    SesionActiva | null  (auth + miembro de equipo)
//   - themeStore:     'dark' | 'light'     (persiste en localStorage)
//   - uiLockStore:    boolean              (bloquea UI durante saves)
//
// Patrón:
//   const unsubscribe = sesionStore.subscribe((sesion) => { ... });
//   sesionStore.set(nuevaSesion);
//   const actual = sesionStore.get();
//   unsubscribe();  // limpiar al destruir el componente
//
// Autor: Simplificalo Consulting LLC
// =====================================================================

import { env, isDevelopment } from '@/core/env';
import type { SesionActiva } from '@/types/domain';

// ─── TIPOS ───────────────────────────────────────────────────────────

/** Listener: callback que recibe el nuevo valor del store. */
type Listener<T> = (value: T) => void;

/** Función de cleanup: se llama para desuscribir un listener. */
type Unsubscribe = () => void;

/** API pública de un store. */
export interface Store<T> {
  /** Lee el valor actual (sincrónico). */
  readonly get: () => T;
  /** Reemplaza el valor completo y notifica a los suscriptores. */
  readonly set: (value: T) => void;
  /** Suscribe un listener. Retorna función de cleanup. */
  readonly subscribe: (listener: Listener<T>) => Unsubscribe;
}

interface CreateStoreOptions<T> {
  /** Nombre para logging en development (no afecta producción). */
  readonly name: string;
  /** Persistir en localStorage con esta key. Si undefined, no persiste. */
  readonly persistKey?: string;
  /**
   * Valida un valor leído de localStorage. Si retorna false, usa default.
   * Sin esto, datos corruptos pueden causar crash al rehidratar.
   */
  readonly validator?: (value: unknown) => value is T;
}

// ─── FACTORY DE STORES ───────────────────────────────────────────────

/**
 * Crea un store observable tipado.
 *
 * @param initialValue Valor inicial si no hay valor persistido.
 * @param options      Configuración (name, persistKey, validator).
 */
const createStore = <T>(initialValue: T, options: CreateStoreOptions<T>): Store<T> => {
  const listeners = new Set<Listener<T>>();

  // Hidratación inicial desde localStorage si aplica.
  let value: T = initialValue;
  if (options.persistKey !== undefined && typeof window !== 'undefined') {
    try {
      const stored = window.localStorage.getItem(options.persistKey);
      if (stored !== null) {
        const parsed: unknown = JSON.parse(stored);
        if (options.validator?.(parsed) ?? false) {
          value = parsed as T;
        } else if (isDevelopment()) {
          console.warn(
            `[store:${options.name}] Valor en localStorage no pasa validator, usando default`,
          );
        }
      }
    } catch (e) {
      // localStorage corrupto o JSON malformado. Default + log.
      if (isDevelopment()) {
        console.warn(`[store:${options.name}] Error leyendo localStorage:`, e);
      }
    }
  }

  const get = (): T => value;

  const set = (newValue: T): void => {
    if (Object.is(value, newValue)) return; // skip si idéntico (referencia)
    const previous = value;
    value = newValue;

    // Persistir si configurado.
    if (options.persistKey !== undefined && typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(options.persistKey, JSON.stringify(newValue));
      } catch (e) {
        // QuotaExceededError o similar. No es fatal: el store sigue
        // funcionando en memoria, solo no se persiste.
        if (isDevelopment()) {
          console.warn(`[store:${options.name}] Error escribiendo localStorage:`, e);
        }
      }
    }

    // Notificar a todos los suscriptores.
    if (isDevelopment() && env.logLevel === 'debug') {
      console.info(`[store:${options.name}] set:`, { previous, current: newValue });
    }
    for (const listener of listeners) {
      listener(newValue);
    }
  };

  const subscribe = (listener: Listener<T>): Unsubscribe => {
    listeners.add(listener);
    return (): void => {
      listeners.delete(listener);
    };
  };

  return Object.freeze({ get, set, subscribe });
};

// ─── STORE: SESIÓN ACTIVA ────────────────────────────────────────────

/**
 * Sesión del usuario actual. null = no autenticado.
 * Lo gestiona `auth.ts` (signIn / signOut / onAuthChange).
 *
 * NO se persiste en localStorage: Supabase Auth ya lo hace por su cuenta.
 * Al bootstrap, auth.ts lee la sesión de Supabase y llama set() aquí.
 */
export const sesionStore: Store<SesionActiva | null> = createStore<SesionActiva | null>(null, {
  name: 'sesion',
});

// ─── STORE: THEME ────────────────────────────────────────────────────

/** Modos de tema soportados. */
export type ThemeMode = 'dark' | 'light';

/**
 * Tema visual del panel. Persiste en localStorage.
 * Cambios disparan re-render del CSS via [data-theme] en <html>.
 */
export const themeStore: Store<ThemeMode> = createStore<ThemeMode>('dark', {
  name: 'theme',
  persistKey: 'rebeca-admin-theme',
  validator: (value): value is ThemeMode => value === 'dark' || value === 'light',
});

// ─── STORE: UI LOCK ──────────────────────────────────────────────────

/**
 * Bloquea la UI durante operaciones críticas (guardar, eliminar, invitar).
 * Las páginas y componentes leen este store para:
 *   - Deshabilitar botones submit
 *   - Mostrar spinner global
 *   - Prevenir doble-submit
 *
 * @example
 *   uiLockStore.set(true);
 *   try {
 *     await saveOperation();
 *   } finally {
 *     uiLockStore.set(false);
 *   }
 */
export const uiLockStore: Store<boolean> = createStore<boolean>(false, {
  name: 'uiLock',
});

// ─── HELPER: aplicar theme al DOM ────────────────────────────────────

/**
 * Aplica el theme actual al elemento <html>.
 * Llamar en bootstrap del app y subscribirse a themeStore para
 * mantener sincronizado.
 *
 * @example
 *   applyTheme(themeStore.get());
 *   themeStore.subscribe(applyTheme);
 */
export const applyTheme = (theme: ThemeMode): void => {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
};

// ─── HELPER: auto-cleanup de subscripciones ──────────────────────────

/**
 * Combina múltiples unsubscribe en uno solo.
 * Útil cuando una página se suscribe a varios stores.
 *
 * @example
 *   const cleanup = combineUnsubscribes(
 *     sesionStore.subscribe(handleSesion),
 *     themeStore.subscribe(handleTheme),
 *   );
 *   // al destruir la página:
 *   cleanup();
 */
export const combineUnsubscribes = (...unsubs: readonly Unsubscribe[]): Unsubscribe => {
  return (): void => {
    for (const unsub of unsubs) {
      unsub();
    }
  };
};
